import React, { forwardRef, FunctionComponent, ReactNode } from 'react';
import { ReactElement } from 'react';

import Head from 'next/head';
import styled from 'styled-components';

import { Text } from 'chime-ui/components/Typography';
import { colors } from 'chime-ui/system';
import media from 'chime-ui/system/media';

type LayoutProps = {
  title?: string;
  children: ReactNode;
  header?: ReactElement;
  footer?: ReactElement;
  showTagline?: boolean;
};

const Layout: FunctionComponent<LayoutProps> = ({
  title = 'Chime - Pay Anyone',
  children,
  header,
  footer,
}) => {
  return (
    <PageWrapper>
      <Head>
        <title>{title}</title>
      </Head>
      <Card>
        {header && header}
        <CardContent>
          <WebkitAnimationLayout>{children}</WebkitAnimationLayout>
        </CardContent>
      </Card>
      {footer ? footer : <Footer>{<CopyrightText />}</Footer>}
    </PageWrapper>
  );
};

export const CopyrightText = () => {
  const date = new Date();
  const year = date.getFullYear();

  return (
    <Text color={colors.darkGrey}>
      © 2013-{year} Chime. All rights reserved.
    </Text>
  );
};

export default Layout;

export const ContentWrapper = styled.div`
  position: relative;
  ${media.tablet`
    margin-bottom: 4.5rem;
  `};
`;

export const Footer = styled.footer`
  display: flex;
  flex-direction: column;
  margin-top: 5rem;

  * {
    text-align: center;
    font-size: 9px;
  }

  ${media.tablet`
    margin-top: 13rem;
  `}
`;

export const ContentLayout = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;

  ${({ fullHeight }) =>
    fullHeight
      ? media.mobileOnly`
          min-height: 75vh;
        `
      : ''}
`;

type ContentProps = React.HTMLProps<HTMLDivElement> & {
  fullHeight?: boolean;
};

export const Content: FunctionComponent<ContentProps> = forwardRef(
  (props, ref) => <ContentLayout ref={ref} fullHeight={true} {...props} />
);

export const SpacedContent = styled(Content)`
  justify-content: flex-start;
  margin-top: 2rem;
`;

export const PageWrapper = styled.main`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  max-width: 37.5rem;
  margin: 0 auto;
  padding: 0 1.5rem;
  padding-bottom: 10vh;

  ${media.tablet`
    padding-top: 65px;
    padding-bottom: 50px;
    padding-left: 0;
    padding-right: 0;
  `};
`;

const Card = styled.div`
  background: ${props => props.theme.colors.white};
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;

  ${media.tablet`
    min-height: 23rem;  
    border-radius: 24px;
    box-shadow: 0 8px 12px 0 rgba(0, 0, 0, 0.08), 0 24px 32px 0 rgba(0, 0, 0, 0.04);
    display: flex;
    flex-direction: column;
  `};
`;

const CardContent = styled.div`
  ${media.tablet`
    padding: 0 8rem 0;
  `};
`;

// Animation might cause the page to scroll
// and this fixes the scrolling issue (See: PFG-579)
const WebkitAnimationLayout = styled.div`
  -webkit-backface-visibility: hidden;
  -webkit-perspective: 1000px;
`;
